import { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { CalendarIcon, HistoryIcon } from 'lucide-react';
import { format } from 'date-fns';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldDescription, FieldGroup, FieldLabel, FieldError } from '@/components/ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { ConsignmentHistoryModal } from '@/widgets/consignment-history-modal';
import { cn } from '@/lib/utils';
import { useProjectSiteParams } from '@/shared/lib';

import { useConsignmentForm } from '../model';
import { PointsOfContactCard } from './points-of-contact-card';
import { StartingPointCard } from './starting-point-card';
import { ServiceIdentificationCard } from './service-identification-card';
import { AttachmentsCard } from './attachments-card';

interface ConsignmentEditFormProps {
  consignmentId: string;
  onSave: (formData: unknown) => void;
}

export interface ConsignmentEditFormRef {
  submit: () => void;
}

export const ConsignmentEditForm = forwardRef<ConsignmentEditFormRef, ConsignmentEditFormProps>(
  ({ consignmentId, onSave }, ref) => {
    const { t } = useTranslation();
    const { siteId } = useProjectSiteParams();
    const { form, onSubmit, isFetchingConsignment, consignmentData } = useConsignmentForm({ consignmentId, onSave });

    const {
      control,
      register,
      handleSubmit,
      formState: { errors }
    } = form;

    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(onSubmit)();
      }
    }));

    return (
      <form onSubmit={handleSubmit(onSubmit)} className="relative space-y-6">
        {isFetchingConsignment && (
          <div className="absolute w-full h-full inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center z-10">
            <Spinner className="h-8 w-8" />
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Consignment Identification */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">{t('request.id.desc')}</CardTitle>
            </CardHeader>
            <CardContent>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="consignmentId">{t('request.id.label')}</FieldLabel>
                  <Input id="consignmentId" value={consignmentId} disabled />
                </Field>
                <Field>
                  <FieldLabel>{t('consignment.fields.locationGroup', 'Location Group')}</FieldLabel>
                  <Input value={`Site ${siteId}`} disabled />
                  <FieldDescription>Current site location</FieldDescription>
                </Field>

                <Field>
                  <FieldLabel>{t('consignment.fields.createdBy', 'Created by')}</FieldLabel>
                  <Input value={consignmentData?.createdByLogon || 'N/A'} disabled />
                </Field>

                <Field>
                  <FieldLabel>{t('consignment.fields.trackingCode', 'Tracking Code')}</FieldLabel>
                  <Input value={consignmentData?.trackIdnf || 'N/A'} disabled />
                </Field>

                <Field>
                  <FieldLabel htmlFor="ownersReference">
                    {t('consignment.fields.ownersReference', "Owner's Reference")}
                  </FieldLabel>
                  <Input id="ownersReference" {...register('ownersReference')} placeholder="Enter owner's reference" />
                  <FieldError errors={[errors.ownersReference]} />
                </Field>
              </FieldGroup>
            </CardContent>
          </Card>

          {/* Starting Point */}
          <StartingPointCard form={form} />

          {/* Service Identification */}
          <ServiceIdentificationCard form={form} />

          {/* Consignment Control */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t('consignment.sections.control', 'Consignment Control')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="status">{t('consignment.fields.status', 'Status')}</FieldLabel>
                  <Controller
                    name="status"
                    control={control}
                    defaultValue="new"
                    render={({ field }) => (
                      <Select value={field.value || 'new'} onValueChange={field.onChange}>
                        <SelectTrigger id="status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">{t('consignment.status.new', 'New')}</SelectItem>
                          <SelectItem value="normal">{t('consignment.status.normal', 'Normal')}</SelectItem>
                          <SelectItem value="urgent">{t('consignment.status.urgent', 'Urgent')}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="priority">{t('consignment.fields.priority', 'Priority')}</FieldLabel>
                  <Controller
                    name="priority"
                    control={control}
                    defaultValue="normal"
                    render={({ field }) => (
                      <Select value={field.value || 'normal'} onValueChange={field.onChange}>
                        <SelectTrigger id="priority">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">{t('consignment.priority.normal', 'Normal')}</SelectItem>
                          <SelectItem value="high">{t('consignment.priority.high', 'High')}</SelectItem>
                          <SelectItem value="urgent">{t('consignment.priority.urgent', 'Urgent')}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </Field>

                <div className="flex justify-end">
                  <ConsignmentHistoryModal
                    consignmentId={consignmentId}
                    trigger={
                      <Button type="button" variant="outline" size="sm">
                        <HistoryIcon className="mr-2 h-4 w-4" />
                        {t('newcmr.showhistory.button')}
                      </Button>
                    }
                  />
                </div>

                <Field>
                  <FieldLabel htmlFor="remarks">{t('consignment.fields.remarks', 'Remarks')}</FieldLabel>
                  <Textarea id="remarks" {...register('remarks')} placeholder="Enter remarks" rows={4} />
                  <FieldError errors={[errors.remarks]} />
                </Field>

                <Field>
                  <FieldLabel htmlFor="transportUnitQuantity">
                    {t('consignment.fields.transportUnitQuantity', 'Transport Unit Quantity')}
                  </FieldLabel>
                  <Input
                    id="transportUnitQuantity"
                    type="number"
                    {...register('transportUnitQuantity', { valueAsNumber: true })}
                    placeholder="Enter quantity"
                  />
                  <FieldError errors={[errors.transportUnitQuantity]} />
                </Field>
              </FieldGroup>
            </CardContent>
          </Card>

          {/* Planned Movement */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t('consignment.sections.plannedMovement', 'Planned Movement')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="onSiteDate">{t('consignment.fields.onSiteDate', 'On-Site Date')}</FieldLabel>
                  <Controller
                    name="onSiteDate"
                    control={control}
                    render={({ field }) => (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            id="onSiteDate"
                            variant="outline"
                            className={cn(
                              'w-full justify-start text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}>
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? format(new Date(field.value), 'PPP') : 'Pick a date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={field.value ? new Date(field.value) : undefined}
                            onSelect={(date) => field.onChange(date?.toISOString())}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  />
                  <FieldError errors={[errors.onSiteDate]} />
                </Field>

                <Field>
                  <FieldLabel htmlFor="plannedMode">{t('consignment.fields.plannedMode', 'Planned Mode')}</FieldLabel>
                  <Controller
                    name="plannedMode"
                    control={control}
                    defaultValue="none"
                    render={({ field }) => (
                      <Select value={field.value || 'none'} onValueChange={field.onChange}>
                        <SelectTrigger id="plannedMode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t('consignment.plannedMode.none', 'None')}</SelectItem>
                          <SelectItem value="air">{t('consignment.plannedMode.air', 'Air')}</SelectItem>
                          <SelectItem value="road">{t('consignment.plannedMode.road', 'Road')}</SelectItem>
                          <SelectItem value="sea">{t('consignment.plannedMode.sea', 'Sea')}</SelectItem>
                          <SelectItem value="rail">{t('consignment.plannedMode.rail', 'Rail')}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="missionTransporterNumber">
                    {t('consignment.fields.missionTransporterNumber', 'Mission or Transporter Number')}
                  </FieldLabel>
                  <Input
                    id="missionTransporterNumber"
                    type="number"
                    {...register('missionTransporterNumber', { valueAsNumber: true })}
                    placeholder="Enter number"
                  />
                  <FieldError errors={[errors.missionTransporterNumber]} />
                </Field>
              </FieldGroup>
            </CardContent>
          </Card>

          {/* SLA/Invoice/Packing List Links */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t('consignment.sections.documents', 'SLA/Invoice/Packing List Links')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {t('consignment.documents.placeholder', 'Document links will be available here')}
              </p>
            </CardContent>
          </Card>

          {/* Points of Contact */}
          <PointsOfContactCard
            control={control}
            watch={form.watch}
            setValue={form.setValue}
            consignmentData={consignmentData}
          />

          {/* Attachments */}
          <AttachmentsCard consignmentId={consignmentId} />
        </div>
      </form>
    );
  }
);
